import { yupResolver } from "@hookform/resolvers/yup";
import * as CheckoutStepTracker from "@omni/services/tracking/trackers/tools/checkout/checkoutStep.tracker";
import { CheckoutStep } from "@omni/types/api/cart/bff/shared/enums/checkoutStep.enum";
import { CloudCustomerAddress } from "@omni/types/api/customer/cloud/address.model";
import { AddressType } from "@omni/types/api/customer/cloud/shared/enums/addressType.enum";
import { AddressValidationStatus } from "@omni/types/api/customer/cloud/shared/enums/addressValidationStatus.enum";
import { ComponentsBoxConditionType } from "@omni/types/components/cart/shared/enums/boxConditionType.enum";
import { ComponentsCustomer } from "@omni/types/components/customer/customer.model";
import { ComponentsCustomerType } from "@omni/types/components/customer/shared/customerType.enum";
import { BonusCardType } from "@omni/types/enums/bonusCardType.enum";
import { Country } from "@omni/types/enums/country.enum";
import { CheckoutStyles } from "@omni/ui/brand/checkout/checkout.styles";
import { Button } from "@omni/ui/components/Button/button.component";
import * as CartUtils from "@omni/utils/cart.utils";
import { isDefined } from "@omni/utils/helper.utils";
import { include } from "@omni/utils/object.utils";
import { cn } from "@omni/utils/styles";
import { FormEvent, useEffect, useMemo } from "react";
import { shallowEqual, useSelector } from "react-redux";

import { useAddressValidationContext } from "@/components/Address/AddressValidation/context/addressValidation.context";
import { CheckoutCustomerAddressesContainer } from "@/components/Checkout/CheckoutCustomer/addresses/addresses.component";
import { AddressBookContextProvider } from "@/components/Checkout/CheckoutCustomer/addresses/context/addressBook.context";
import { CheckoutBonusCardAvailableFormBlock } from "@/components/Checkout/CheckoutCustomer/bonusCardAvailable/bonusCardAvailable.component";
import { BoxConditionsComponent } from "@/components/Checkout/CheckoutCustomer/boxConditions/boxConditions.container";
import { CheckoutCustomerDetailsContainer } from "@/components/Checkout/CheckoutCustomer/details/details.component";
import { Store } from "@/components/Checkout/CheckoutCustomer/store/store.component";
import { parseToBPostFieldAddress } from "@/components/Form/RHF/FormFields/BPostAddressField/helpers/address.helper";
import { CheckBoxField } from "@/components/Form/RHF/FormFields/CheckboxField/CheckBoxField.component";
import { useOmniForm } from "@/components/Form/RHF/Utils/useOmniForm";
import { useThunkDispatch } from "@/redux/dispatcher";
import * as AddressModule from "@/redux/modules/api/data/address/address.module";
import { CreateAddress } from "@/redux/modules/api/data/address/address.types";
import * as AddressBookSelectors from "@/redux/modules/api/data/addressbook/addressbook.selectors";
import { CheckoutStepValidationResult } from "@/redux/modules/api/data/cart/@types/cart.types";
import * as CartModule from "@/redux/modules/api/data/cart/cart.module";
import * as CartSelectors from "@/redux/modules/api/data/cart/cart.selectors";
import * as CustomerModule from "@/redux/modules/api/data/customer/customer.module";
import * as CustomerSelectors from "@/redux/modules/api/data/customer/customer.selectors";
import * as PhonenumberPrefixesSelectors from "@/redux/modules/api/data/phonenumberPrefixes/phonenumberPrefixes.selectors";
import * as CheckoutCustomerDetailsFormModule from "@/redux/modules/value/checkout/customerDetails/checkoutCustomerDetailsForm.module";
import * as CheckoutCustomerDetailsFormSelectors from "@/redux/modules/value/checkout/customerDetails/checkoutCustomerDetailsForm.selectors";
import { broadcastMessage } from "@/redux/modules/value/messages/messages.utils";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import {
    GetStoreStrategy,
    StorePersistenceStrategy,
    StoreService
} from "@/services/store.service";
import {
    getAllowedDeliveryAddresses,
    getDeliveryAddress,
    getEmptyAddress,
    getPaymentAddress
} from "@/utils/address/address";
import { AddressToValidate } from "@/utils/address/validation.types";
import { getUserInfo } from "@/utils/analytics/util/enrich";
import * as BonusCardUtils from "@/utils/bonusCard";
import { Config } from "@/utils/config.utils";
import { FormInfoProvider } from "@/utils/forms/formInfo.provider";
import { OmniFormContextProvider } from "@/utils/forms/omniForm.provider";
import { mapSeries } from "@/utils/helpers";
import { useIsECorner } from "@/utils/hooks/useIsECorner.hook";

import { useCheckout } from "../useCheckout.hook";
import { useCheckoutCustomerContext } from "./checkoutCustomer.context";
import {
    CheckoutCustomerFormValues,
    CheckoutCustomerValidationSchema
} from "./checkoutCustomer.schema";
import {
    AddressToCreate,
    createCheckoutAddresses,
    resolveCheckoutAddresses,
    resolveCreatedAddresses
} from "./helpers/checkoutAddresses.helper";
import { TermsOfServiceCheckField } from "./termsOfService/termsOfServiceCheck.container";
import { TermsOfServiceCheckDocument } from "./termsOfService/termsOfServiceCheck.schema";

const FORM_ID = "checkoutCustomerDetails";

const SKIP_CHECKOUT = "SKIP_CHECKOUT";

const CheckoutCustomer = () => {
    const { t } = useGranite();
    const { validate, isValidating } = useAddressValidationContext();
    const { additionalServiceProductDocuments, customer } =
        useCheckoutCustomerContext();

    const { isECorner } = useIsECorner();

    const dispatch = useThunkDispatch();
    const isCustomerFilledIn = useSelector(
        CustomerSelectors.isCustomerFilledIn
    );
    const addressBookAddresses = useSelector(
        AddressBookSelectors.getAddresses,
        shallowEqual
    );
    const phonenumberPrefixes = useSelector(
        PhonenumberPrefixesSelectors.getPhonenumberPrefixes,
        shallowEqual
    );
    const checkoutCustomerDetailsForm = useSelector(
        CheckoutCustomerDetailsFormSelectors.getCheckoutCustomerDetailsForm
    );
    const isLoadingCheckoutCustomerDetailsForm = useSelector(
        CheckoutCustomerDetailsFormSelectors.isLoading
    );

    const isClickAndCollect = useSelector(CartSelectors.hasClickAndCollect);
    const isHomeDelivery = useSelector(CartSelectors.hasHomeDelivery);
    const isClickAndCollectOnly = useSelector(
        CartSelectors.isClickAndCollectOnly
    );

    const customerLoadedSuccessfully = useSelector(
        CustomerSelectors.isSuccessfullyLoaded
    );
    const addressBookLoadedSuccessfully = useSelector(
        AddressBookSelectors.isSuccessfullyLoaded
    );

    const deliveryAddressBookAddresses =
        getAllowedDeliveryAddresses(addressBookAddresses);

    const { cart, cartLoadedSuccessfully, cartLoading, validateCheckoutStep } =
        useCheckout();

    const deliveryAddressId = useMemo(() => {
        return isHomeDelivery
            ? (checkoutCustomerDetailsForm?.deliveryAddressId ??
                  getDeliveryAddress(addressBookAddresses, cart)?.id)
            : undefined;
    }, [
        isHomeDelivery,
        checkoutCustomerDetailsForm,
        addressBookAddresses,
        cart
    ]);

    const paymentAddressId = useMemo(() => {
        return (
            checkoutCustomerDetailsForm?.paymentAddressId ??
            getPaymentAddress(addressBookAddresses, cart)?.id
        );
    }, [checkoutCustomerDetailsForm, addressBookAddresses, cart]);

    const getDefaultAddressValues = () => {
        // Create empty address object for the addresses of there is no address book
        if (addressBookAddresses.length === 0) {
            const samePaymentAddress = isHomeDelivery;

            return {
                samePaymentAddress: samePaymentAddress,
                [AddressType.Delivery]: getEmptyAddress(AddressType.Delivery),
                [AddressType.Payment]: getEmptyAddress(AddressType.Payment)
            };
        } else {
            const deliveryAddress = getDeliveryAddress(
                addressBookAddresses,
                cart
            );
            const deliveryBPostAddress = deliveryAddress
                ? parseToBPostFieldAddress(deliveryAddress)
                : getEmptyAddress(AddressType.Delivery);

            const paymentAddress = getPaymentAddress(
                addressBookAddresses,
                cart
            );
            const paymentBPostAddress = paymentAddress
                ? parseToBPostFieldAddress(paymentAddress)
                : getEmptyAddress(AddressType.Payment);

            const samePaymentAddress = isHomeDelivery
                ? deliveryAddressId === paymentAddressId || !paymentAddressId
                : false;

            return {
                samePaymentAddress: samePaymentAddress,
                [AddressType.Delivery]: deliveryBPostAddress,
                [AddressType.Payment]: samePaymentAddress
                    ? undefined
                    : paymentBPostAddress
            };
        }
    };

    const getInitialFormValues = () => {
        const { delivery, payment, samePaymentAddress } =
            getDefaultAddressValues();

        const termsOfServiceChecks: Array<TermsOfServiceCheckDocument> =
            additionalServiceProductDocuments.map((document) => ({
                file: document.file,
                title: document.title,
                checked: document.checked
            }));
        const activeBoxConditions = CartUtils.getActiveBoxConditions(cart);

        const ageCondition = activeBoxConditions.find(
            (c) => c.name === ComponentsBoxConditionType.AgeCondition
        );
        const partnerCondition = activeBoxConditions.find(
            (c) => c.name === ComponentsBoxConditionType.PartnerConditions
        );

        const vatInfo =
            customer?.customerType === ComponentsCustomerType.B2B
                ? {
                      companyName: customer?.companyName ?? "",
                      vatNr: customer?.vatNr ?? "",
                      vatNrPrefix: customer?.vatNrPrefix ?? ""
                  }
                : undefined;

        const bonusCardAvailable =
            !!cart?.bonusCardNumber &&
            !BonusCardUtils.isType(
                BonusCardType.Electronic,
                cart?.bonusCardNumber
            );

        return {
            hasHomeDelivery: !!isHomeDelivery,
            storeId: StoreService.getStoreCode(GetStoreStrategy.Checkout) || "",
            requestInvoice:
                isHomeDelivery ||
                customer?.customerType === ComponentsCustomerType.B2B ||
                customer?.requestInvoice,
            bonusCard: cart?.bonusCardNumber
                ? { number: cart.bonusCardNumber }
                : undefined,
            bonusCardAvailable: bonusCardAvailable ? "true" : "false",
            customerType: customer?.customerType ?? ComponentsCustomerType.B2C,
            firstName: customer?.firstName ?? "",
            lastName: customer?.lastName ?? "",
            phone: customer?.phone ?? "",
            phonePrefix: customer?.phonePrefix ?? "+32",
            delivery,
            payment,
            samePaymentAddress,
            receiveSms: customer?.receiveSms ?? false,
            storeIdWasInvalid: false,
            isAgeConditionRequired: !!ageCondition?.required,
            isPartnerConditionRequired: !!partnerCondition?.required,
            boxConditions: {
                ageCondition: !!ageCondition?.checked,
                partnerCondition: !!partnerCondition?.checked
            },
            termsOfServiceChecks,
            vatInfo
        } satisfies CheckoutCustomerFormValues;
    };

    const methods = useOmniForm({
        mode: "onBlur",
        reValidateMode: "onBlur",
        defaultValues: getInitialFormValues(),
        resolver: yupResolver(CheckoutCustomerValidationSchema),
        context: {
            phonenumberPrefixData: phonenumberPrefixes,
            customerBonusCard: customer?.bonusCardNumber
        }
    });

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        if (
            cartLoadedSuccessfully &&
            customerLoadedSuccessfully &&
            addressBookLoadedSuccessfully &&
            cart
        ) {
            CheckoutStepTracker.trackUserInfoStart(
                cart,
                getUserInfo().clientID
            );
        }
    }, [
        cartLoadedSuccessfully,
        customerLoadedSuccessfully,
        addressBookLoadedSuccessfully
    ]);

    useEffect(() => {
        if (
            isDefined(deliveryAddressId) &&
            checkoutCustomerDetailsForm?.deliveryAddressId !== deliveryAddressId
        ) {
            dispatch(
                CheckoutCustomerDetailsFormModule.update({
                    deliveryAddressId: deliveryAddressId
                })
            );
        }
    }, [checkoutCustomerDetailsForm, deliveryAddressId, dispatch]);

    useEffect(() => {
        if (
            isDefined(paymentAddressId) &&
            checkoutCustomerDetailsForm?.paymentAddressId !== paymentAddressId
        ) {
            dispatch(
                CheckoutCustomerDetailsFormModule.update({
                    paymentAddressId: paymentAddressId
                })
            );
        }
    }, [checkoutCustomerDetailsForm, paymentAddressId, dispatch]);

    const handleSubmit = (formObject: CheckoutCustomerFormValues) => {
        // Set the state of the for to loading, so the button has the loading indication
        dispatch(
            CheckoutCustomerDetailsFormModule.update({
                isLoading: true
            })
        );

        // force the requestInvoice to be true is it's a C&C only and B2B customer
        formObject.requestInvoice =
            isClickAndCollectOnly &&
            (isCustomerFilledIn
                ? customer?.customerType === ComponentsCustomerType.B2B
                : formObject.customerType === ComponentsCustomerType.B2B)
                ? true
                : formObject.requestInvoice;

        // Handle the promises, and do the checkout call with the extra needed fields
        return Promise.all([
            prepareCustomerPromise(formObject),
            prepareAddressesPromise(formObject)
        ])
            .then(async (actions) => {
                const errorRequest = actions.find((action) =>
                    (action.type || "").includes("failure")
                );
                if (errorRequest) {
                    dispatch(
                        CheckoutCustomerDetailsFormModule.update({
                            isLoading: false
                        })
                    );
                    broadcastMessage({
                        id: "shoppingCart-error",
                        text: errorRequest.payload.message,
                        status: "danger",
                        dismissible: true
                    });
                    return Promise.reject(SKIP_CHECKOUT);
                }

                const actionDeliveryAddress =
                    actions[1].payload.addresses.deliveryAddress;
                const actionDeliveryAddressId =
                    actions[1].payload.addresses.deliveryAddressId ??
                    actionDeliveryAddress?.id;
                const actionPaymentAddress =
                    actions[1].payload.addresses.paymentAddress;
                const actionPaymentAddressId =
                    actions[1].payload.addresses.paymentAddressId ??
                    actionPaymentAddress?.id;

                const {
                    deliveryAddress,
                    deliveryAddressId,
                    paymentAddress,
                    paymentAddressId
                } = getAddressesFromAction(
                    actionDeliveryAddress,
                    actionDeliveryAddressId,
                    actionPaymentAddress,
                    actionPaymentAddressId,
                    !!formObject.samePaymentAddress
                );

                const addressesToValidate = getAddressesToValidate(
                    deliveryAddress,
                    deliveryAddressId,
                    paymentAddress,
                    paymentAddressId
                );

                const resolveOptions = {
                    isHomeDelivery: !!isHomeDelivery,
                    samePaymentAddress: !!formObject.samePaymentAddress,
                    deliveryAddressId,
                    paymentAddressId
                };

                if (addressesToValidate.length === 0) {
                    return resolveCheckoutAddresses({
                        ...resolveOptions,
                        deliveryAddress,
                        paymentAddress
                    });
                } else {
                    return await validateAddresses(addressesToValidate).then(
                        () =>
                            resolveCheckoutAddresses({
                                ...resolveOptions,
                                deliveryAddress: addressesToValidate.find(
                                    (a) => a.type === AddressType.Delivery
                                ),
                                paymentAddress: addressesToValidate.find(
                                    (a) => a.type === AddressType.Payment
                                )
                            })
                    );
                }
            })
            .then(
                ({
                    deliveryAddress,
                    deliveryAddressId,
                    paymentAddress,
                    paymentAddressId
                }) => {
                    const additionalServiceTermsOfServiceChecks =
                        additionalServiceProductDocuments
                            .map(({ file }) => file.code)
                            .filter((code) => {
                                const file =
                                    formObject.termsOfServiceChecks.find(
                                        (f) => f.file.code === code
                                    );
                                return file?.checked;
                            });

                    // Do the actual submit of the checkout step
                    return dispatch(
                        CartModule.checkout(
                            formObject.storeId,
                            !!formObject.boxConditions.ageCondition,
                            !!formObject.boxConditions.partnerCondition,
                            formObject.bonusCard?.number,
                            deliveryAddress?.id ??
                                deliveryAddressId ??
                                undefined,
                            paymentAddress?.id ?? paymentAddressId ?? undefined,
                            additionalServiceTermsOfServiceChecks
                        )
                    ).then((action) => {
                        if (action.type === CartModule.APIStatus.SUCCESS) {
                            return location.assign(
                                Config.pages[
                                    `checkout-${action.payload.checkoutStep as CheckoutStep}`
                                ]
                            );
                        } else if (
                            action.type === CartModule.APIStatus.FAILURE
                        ) {
                            broadcastMessage({
                                id: "shoppingCart-error",
                                text: action.payload.message,
                                status: "danger",
                                dismissible: true
                            });
                        }
                    });
                }
            )
            .catch((error) => {
                if (error === SKIP_CHECKOUT) {
                    return;
                }
                throw error;
            })
            .finally(() => {
                // Set the loading state to false
                dispatch(
                    CheckoutCustomerDetailsFormModule.update({
                        isLoading: false
                    })
                );
            });
    };

    const prepareCustomerPromise = (formObject: CheckoutCustomerFormValues) => {
        // If the inline form is visible, use the formObject, else use the redux customer
        if (!isCustomerFilledIn) {
            return createCustomer({
                ...customer!,
                ...formObject,
                ...include(formObject.vatInfo, (obj) => ({
                    companyName: obj.companyName,
                    vatNr: obj.vatNr,
                    vatNrPrefix: obj.vatNrPrefix
                }))
            });
        } else {
            // Get the redux customer (redux customer can differ from the formObject customer) and merge it with the needed fields
            // TODO change this method when the customer service had changed it's PATCH method
            const {
                receiveSms,
                requestInvoice,
                bonusCard,
                storeId,
                storeIdWasInvalid
            } = formObject;

            const mergedCustomer = {
                ...customer!,
                receiveSms: !!receiveSms,
                requestInvoice: !!requestInvoice,
                bonusCardNumber: bonusCard?.number
            } satisfies ComponentsCustomer;

            if (storeIdWasInvalid && storeId) {
                // overwrite storeId when the previous id was invalid (ex: store permanently closed)
                mergedCustomer.favouriteStoreId = storeId;
                StoreService.setStoreCode(
                    StorePersistenceStrategy.Preferred,
                    storeId
                );
            }
            return createCustomer(mergedCustomer);
        }
    };

    const prepareAddressesPromise = async (
        formObject: CheckoutCustomerFormValues
    ) => {
        const addressesToCreate: Array<AddressToCreate> = [];
        const addressIds: {
            deliveryAddressId: null | string;
            paymentAddressId: null | string;
        } = {
            deliveryAddressId: null,
            paymentAddressId: null
        };

        const { deliveryAddressId, paymentAddressId } =
            checkoutCustomerDetailsForm ?? {};

        // Check if a delivery address is needed, else return null
        if (isHomeDelivery) {
            // Check for the addresses in the address book if none create an address, else return the redux state address id
            if (deliveryAddressBookAddresses.length === 0) {
                addressesToCreate.push({
                    type: AddressType.Delivery,
                    isBpostField: !!formObject[AddressType.Delivery]?.selected,
                    address: getAddressObjectByType(
                        formObject[AddressType.Delivery]!,
                        AddressType.Delivery
                    )
                });
            } else if (isDefined(deliveryAddressId)) {
                addressIds.deliveryAddressId = deliveryAddressId;
            }
        }

        // Check if a payment address is needed, else return null
        if (
            (isHomeDelivery && !formObject.samePaymentAddress) ||
            (isClickAndCollectOnly && formObject.requestInvoice)
        ) {
            // Check for the addresses in the address book if none create a address, else return the redux state address id
            if (addressBookAddresses.length === 0) {
                addressesToCreate.push({
                    type: AddressType.Payment,
                    isBpostField: !!formObject[AddressType.Payment]?.selected,
                    address: getAddressObjectByType(
                        formObject[AddressType.Payment]!,
                        AddressType.Payment
                    )
                });
            } else if (isDefined(paymentAddressId)) {
                addressIds.paymentAddressId = paymentAddressId ?? null;
            }
        }

        return createAddresses(addressesToCreate).then(({ type, payload }) => ({
            type: type,
            payload: {
                message: payload.message,
                addresses: resolveCreatedAddresses(
                    payload.addresses,
                    addressIds
                )
            }
        }));
    };

    const getAddressesFromAction = (
        actionDeliveryAddress: CloudCustomerAddress | undefined,
        actionDeliveryAddressId: string | null | undefined,
        actionPaymentAddress: CloudCustomerAddress | undefined,
        actionPaymentAddressId: string | null | undefined,
        samePaymentAddress: boolean
    ) => ({
        deliveryAddress: isHomeDelivery ? actionDeliveryAddress : undefined,
        deliveryAddressId: isHomeDelivery ? actionDeliveryAddressId : undefined,
        paymentAddress:
            isHomeDelivery && samePaymentAddress
                ? actionDeliveryAddress
                : actionPaymentAddress,
        paymentAddressId:
            isHomeDelivery && samePaymentAddress
                ? actionDeliveryAddressId
                : actionPaymentAddressId
    });

    const getAddressesToValidate = (
        deliveryAddress: CloudCustomerAddress | undefined,
        deliveryAddressId: string | null | undefined,
        paymentAddress: CloudCustomerAddress | undefined,
        paymentAddressId: string | null | undefined
    ) => {
        const addressesToValidate: Array<AddressToValidate> = [];

        if (deliveryAddressId) {
            let address = addressBookAddresses.find(
                (a) => a.id === deliveryAddressId
            );
            if (!address && deliveryAddress) {
                address = deliveryAddress;
            }
            if (address) {
                addressesToValidate.push({
                    ...address,
                    isBpostField: false,
                    currentStatus:
                        address.validationStatus ||
                        AddressValidationStatus.Unknown,
                    type: AddressType.Delivery
                });
            }
        }

        if (paymentAddressId && paymentAddressId !== deliveryAddressId) {
            let address = addressBookAddresses.find(
                (a) => a.id === paymentAddressId
            );
            if (!address && paymentAddress) {
                address = paymentAddress;
            }
            if (address) {
                addressesToValidate.push({
                    ...address,
                    isBpostField: false,
                    currentStatus:
                        address.validationStatus ||
                        AddressValidationStatus.Unknown,
                    type: AddressType.Payment
                });
            }
        }

        return addressesToValidate;
    };

    const getAddressObjectByType = <
        T extends AddressType.Delivery | AddressType.Payment
    >(
        address: NonNullable<CheckoutCustomerFormValues[T]>,
        addressType: T
    ): CreateAddress => {
        const isSelected = !!address?.selected;

        let newStatus = isSelected
            ? AddressValidationStatus.Validated
            : address?.validationStatus || AddressValidationStatus.Unknown;

        if (address?.country !== Country.Be) {
            newStatus = AddressValidationStatus.NoValidationNeeded;
        }

        return {
            ...address,
            box: address?.box || "",
            onlyApplicableAsDeliveryAddress:
                addressType === AddressType.Delivery,
            onlyApplicableAsPaymentAddress: addressType === AddressType.Payment,
            validationStatus: newStatus
        } satisfies CreateAddress;
    };

    const createCustomer = (customerData: ComponentsCustomer) =>
        dispatch(CustomerModule.updateFromData(customerData));

    const createAddresses = (addresses: Array<AddressToCreate>) =>
        createCheckoutAddresses({
            addresses,
            validate: validateAddresses,
            create: (addressesToCreate) =>
                dispatch(AddressModule.create(addressesToCreate))
        });

    const validateAddresses = async (addresses: Array<AddressToValidate>) => {
        const result = await mapSeries(addresses, validate);
        if (
            result.some(
                (a) => a.validationStatus === AddressValidationStatus.Closed
            )
        ) {
            throw SKIP_CHECKOUT;
        }

        return result;
    };

    const createSubmitHandler =
        (formHandler: (e?: React.BaseSyntheticEvent) => Promise<void>) =>
        async (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();

            const validationResult = await validateCheckoutStep(
                CheckoutStep.account,
                true
            );

            if (validationResult === CheckoutStepValidationResult.Valid) {
                formHandler(event);
            }
        };

    return (
        <FormInfoProvider
            formId={FORM_ID}
            withNeverbounce={false}
        >
            {() => (
                <OmniFormContextProvider {...methods}>
                    <form
                        id={FORM_ID}
                        onSubmit={createSubmitHandler(
                            methods.handleSubmit(handleSubmit)
                        )}
                    >
                        <section className={cn("tw:mb-6")}>
                            <h2 className={cn("tw:mt-0")}>
                                {t(
                                    "checkout.customerDetails.customerInfo.title"
                                )}
                            </h2>
                            <hr />
                            <CheckoutCustomerDetailsContainer />
                            <CheckBoxField<CheckoutCustomerFormValues>
                                name={"receiveSms"}
                                required={false}
                                label={t(
                                    "checkout.customerDetails.smsConfirmation.label"
                                )}
                            />
                        </section>
                        <AddressBookContextProvider>
                            <CheckoutCustomerAddressesContainer />
                        </AddressBookContextProvider>
                        {!isClickAndCollect && !isECorner && (
                            <Store excludeStores={["5021"]} /> //https://redmine.hubo.be/issues/59164
                        )}
                        <section className={cn("tw:mb-6")}>
                            <h2>
                                {t("checkout.customerDetails.bonusCard.title")}
                            </h2>
                            <hr />
                            <p>
                                <i>
                                    {t(
                                        "checkout.customerDetails.bonusCard.subtitle"
                                    )}
                                </i>
                            </p>
                            <CheckoutBonusCardAvailableFormBlock />
                        </section>

                        <BoxConditionsComponent />

                        <TermsOfServiceCheckField<CheckoutCustomerFormValues> />

                        <hr />
                        <div className={cn("tw:text-right")}>
                            <Button
                                test={"checkoutCustomer-form-button-submit"}
                                color={
                                    CheckoutStyles.submitButton?.color ||
                                    "success"
                                }
                                type={"submit"}
                                size={"lg"}
                                form={"checkoutCustomerDetails"}
                                className={cn("tw:w-full tw:md:w-auto")}
                                loading={
                                    isLoadingCheckoutCustomerDetailsForm ||
                                    isValidating ||
                                    cartLoading
                                }
                            >
                                {t("checkout.customerDetails.button.submit")}
                            </Button>
                        </div>
                    </form>
                </OmniFormContextProvider>
            )}
        </FormInfoProvider>
    );
};

export { CheckoutCustomer };
